import React, { useState } from 'react';
import axios from 'axios';
import { Scan, CheckCircle, XCircle, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const TRCSRN = () => {
  const [stnQR, setStnQR] = useState('');
  const [stnData, setStnData] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [mismatchDevices, setMismatchDevices] = useState([]);
  const [receiverRemarks, setReceiverRemarks] = useState('');
  const [loading, setLoading] = useState(false);

  const handleScanSTN = async () => {
    if (!stnQR.trim()) {
      alert('Please enter STN QR code');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/trc/stn/${stnQR}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStnData(response.data.stn);
        // By default, all devices are selected
        setSelectedDevices(response.data.stn.devices.map(d => d.did));
      }
    } catch (error) {
      console.error('Error fetching STN:', error);
      alert('STN not found or error loading data');
    } finally {
      setLoading(false);
    }
  };

  const toggleDevice = (did) => {
    if (selectedDevices.includes(did)) {
      setSelectedDevices(selectedDevices.filter(d => d !== did));
      if (!mismatchDevices.includes(did)) {
        setMismatchDevices([...mismatchDevices, did]);
      }
    } else {
      setSelectedDevices([...selectedDevices, did]);
      setMismatchDevices(mismatchDevices.filter(d => d !== did));
    }
  };

  const handleReceive = async () => {
    if (selectedDevices.length === 0) {
      alert('Please select at least one device to receive');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/trc/srn/receive`,
        {
          stn_id: stnData.stn_id,
          received_devices: selectedDevices,
          mismatch_devices: mismatchDevices,
          receiver_remarks: receiverRemarks
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`SRN Created: ${response.data.srn_id}\n${response.data.trc_devices_created} devices added to TRC`);
        // Reset
        setStnQR('');
        setStnData(null);
        setSelectedDevices([]);
        setMismatchDevices([]);
        setReceiverRemarks('');
      }
    } catch (error) {
      console.error('Error receiving STN:', error);
      alert('Error creating SRN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Package className="w-8 h-8 text-green-600" />
          <h1 className="text-3xl font-bold text-gray-900">TRC - Receive STN (Create SRN)</h1>
        </div>

        {!stnData ? (
          <div className="bg-white rounded-lg shadow p-8">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Scan STN QR Code
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                value={stnQR}
                onChange={(e) => setStnQR(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleScanSTN()}
                placeholder="Scan STN QR code..."
                className="flex-1 px-4 py-3 border-2 rounded-lg text-lg focus:ring-2 focus:ring-green-500"
              />
              <button
                onClick={handleScanSTN}
                disabled={loading}
                className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 flex items-center gap-2"
              >
                <Scan className="w-5 h-5" />
                {loading ? 'Loading...' : 'Scan'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* STN Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">STN Details</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">STN ID:</span>
                  <p className="font-bold text-lg text-blue-600">{stnData.stn_id}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Source Type:</span>
                  <p className="font-semibold">{stnData.source_type}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Total Devices:</span>
                  <p className="font-semibold">{stnData.total_devices}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Status:</span>
                  <p className="font-semibold text-orange-600">{stnData.status}</p>
                </div>
              </div>
              {stnData.remarks && (
                <div className="mt-4 p-3 bg-blue-50 rounded">
                  <span className="text-sm text-gray-600">Remarks:</span>
                  <p className="text-sm">{stnData.remarks}</p>
                </div>
              )}
            </div>

            {/* Devices List */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Devices</h2>
                <div className="flex gap-4">
                  <span className="text-sm">
                    <CheckCircle className="w-4 h-4 inline text-green-600 mr-1" />
                    Received: {selectedDevices.length}
                  </span>
                  <span className="text-sm">
                    <XCircle className="w-4 h-4 inline text-red-600 mr-1" />
                    Mismatch: {mismatchDevices.length}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                {stnData.devices.map((device, index) => {
                  const isSelected = selectedDevices.includes(device.did);
                  const isMismatch = mismatchDevices.includes(device.did);

                  return (
                    <div
                      key={index}
                      onClick={() => toggleDevice(device.did)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? 'border-green-500 bg-green-50'
                          : isMismatch
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-bold text-gray-900">{device.did}</div>
                          <div className="text-sm text-gray-600">
                            {device.brand} {device.model} | {device.category}
                          </div>
                          <div className="text-xs text-gray-500">
                            SKU: {device.sku} | ZUPC: {device.zupc}
                          </div>
                        </div>
                        <div>
                          {isSelected ? (
                            <CheckCircle className="w-8 h-8 text-green-600" />
                          ) : (
                            <XCircle className="w-8 h-8 text-red-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Receiver Remarks */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Receiver Remarks (Optional)
              </label>
              <textarea
                value={receiverRemarks}
                onChange={(e) => setReceiverRemarks(e.target.value)}
                rows="3"
                placeholder="Any issues or notes..."
                className="w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => setStnData(null)}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReceive}
                disabled={loading || selectedDevices.length === 0}
                className="flex-1 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-300"
              >
                {loading ? 'Receiving...' : `Receive ${selectedDevices.length} Devices`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TRCSRN;
